import { useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import { LayoutDashboard, CreditCard, Users, Settings, ChevronDown, Globe, LogOut, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { PortalShell, PortalShellNavItem } from '@/components/layouts/portal-shell';
import { useCustomerAuth } from '@/contexts/CustomerAuthContext';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { supportedLanguages } from '@/lang';
import { useBrandLogo } from '@/hooks/useBrandLogo';
import { useCustomerPortalMenu } from '@/hooks/customer/useCustomerPortalMenu';
import { useCustomerTeams } from '@/hooks/customer/useCustomerTeams';
import { resolvePortalMenuIcon } from '@/lib/portal-menu-icons';

type NavigationConfig = Omit<PortalShellNavItem, 'label'> & { labelKey: string };

const baseNavigation: NavigationConfig[] = [
  {
    labelKey: 'customer.navigation.overview',
    href: '/customer',
    icon: LayoutDashboard,
    isActive: (pathname) => pathname === '/customer',
  },
  {
    labelKey: 'customer.navigation.billing',
    href: '/customer/billing',
    icon: CreditCard,
  },
  {
    labelKey: 'customer.navigation.teams',
    href: '/customer/teams',
    icon: Users,
  },
  {
    labelKey: 'customer.navigation.settings',
    href: '/customer/settings',
    icon: Settings,
  },
];

export function CustomerLayout() {
  const { user, provider, logout, isLoading, hasRole, isAuthenticated } = useCustomerAuth();
  const brandLogo = useBrandLogo();
  const { t, i18n } = useTranslation();
  const teamsQuery = useCustomerTeams();
  const hasOwnerRole = hasRole(['owner', 'customer_owner']);
  const hasAdminRole = hasRole('customer_admin');
  const hasIndividualRole = hasRole(['customer_individual', 'individual']);
  const hasTeamRoleFromMembership = (teamsQuery.data ?? []).some(
    (team) => team.role === 'owner' || team.role === 'admin',
  );
  const hasTeamOwnerMembership = (teamsQuery.data ?? []).some((team) => team.role === 'owner');
  const hasNoTeamMembership = !teamsQuery.isLoading && (teamsQuery.data ?? []).length === 0;
  const hasUnspecifiedCustomerRole = !user?.role && (user?.roles?.length ?? 0) === 0;
  const showTeamsNav = !hasIndividualRole && (hasOwnerRole || hasAdminRole || hasTeamRoleFromMembership);
  const showBillingNav =
    hasOwnerRole ||
    hasIndividualRole ||
    hasTeamOwnerMembership ||
    (hasUnspecifiedCustomerRole && hasNoTeamMembership);
  const portalMenuQuery = useCustomerPortalMenu(isAuthenticated && !isLoading);

  const portalMenuItems = useMemo<PortalShellNavItem[]>(() => {
    const items = portalMenuQuery.data?.items ?? [];
    if (!items.length) {
      return [];
    }

    return [...items]
      .map((item, index) => ({
        item,
        index,
        order: typeof item.order === 'number' ? item.order : Number.POSITIVE_INFINITY,
      }))
      .sort((a, b) => {
        if (a.order !== b.order) {
          return a.order - b.order;
        }
        return a.index - b.index;
      })
      .map(({ item }) => ({
        label: item.label,
        href: item.url,
        icon: resolvePortalMenuIcon(item.icon),
        external: true,
        target: item.target ?? '_blank',
        isActive: () => false,
      }));
  }, [portalMenuQuery.data]);

  const navigation = useMemo<PortalShellNavItem[]>(() => {
    const translatedNav = baseNavigation.map<PortalShellNavItem>((item) => ({
      ...item,
      label: t(item.labelKey),
    }));

    const trimmedNav = translatedNav.filter((item) => {
      if (item.href === '/customer/teams' && !showTeamsNav) return false;
      if (item.href === '/customer/billing' && !showBillingNav) return false;
      return true;
    });
    const overviewIndex = trimmedNav.findIndex((item) => item.href === '/customer');

    if (!portalMenuItems.length) {
      return trimmedNav;
    }

    if (overviewIndex === -1) {
      return [...portalMenuItems, ...trimmedNav];
    }

    return [
      ...trimmedNav.slice(0, overviewIndex + 1),
      ...portalMenuItems,
      ...trimmedNav.slice(overviewIndex + 1),
    ];
  }, [portalMenuItems, showBillingNav, showTeamsNav, t]);

  const initials = user?.name
    ? user.name
        .split(' ')
        .filter(Boolean)
        .map((part) => part[0]!.toUpperCase())
        .slice(0, 2)
        .join('')
    : 'CU';

  const brandName = provider?.name ?? provider?.slug ?? t('customer.dashboard.providerFallback');
  const currentLanguage =
    supportedLanguages.find((lang) => lang.code === i18n.language) || supportedLanguages[0];

  const changeLanguage = (language: string) => {
    i18n.changeLanguage(language);
  };

  return (
    <PortalShell
      navigation={navigation}
      logoHref="/customer"
      logo={
        brandLogo ? (
          <img
            src={brandLogo}
            alt={`${brandName} logo`}
            className="h-6 w-auto max-w-[140px] object-contain"
          />
        ) : (
          <span className="text-lg font-semibold tracking-tight text-foreground">
            {brandName}
          </span>
        )
      }
      headerRight={
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative flex h-8 items-center gap-2 px-2">
                <Avatar className="h-8 w-8">
                  <AvatarFallback>{initials}</AvatarFallback>
                </Avatar>
                <span className="hidden text-sm font-medium md:block">
                  {user?.name ?? t('customer.portal.accountFallback')}
                </span>
                <ChevronDown className="ml-1 hidden h-4 w-4 text-muted-foreground md:block" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <div className="flex items-center gap-3 px-2 py-2">
                <Avatar className="h-10 w-10">
                  <AvatarFallback>{initials}</AvatarFallback>
                </Avatar>
                <div className="flex flex-col">
                  <span className="font-medium">{user?.name ?? t('customers.customer')}</span>
                  <span className="text-xs text-muted-foreground">{user?.email}</span>
                </div>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                {t('navigation.account')}
              </DropdownMenuLabel>
              <DropdownMenuItem className="gap-2" disabled>
                <User className="h-4 w-4" />
                <span>{t('customer.portal.profileComingSoon')}</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />

              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="gap-2">
                  <Globe className="h-4 w-4" />
                  <span>{t('navigation.language')}</span>
                  <span className="ml-auto text-xs">
                    {currentLanguage.flag} {currentLanguage.name}
                  </span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  <DropdownMenuRadioGroup value={i18n.language} onValueChange={changeLanguage}>
                    {supportedLanguages.map((language) => (
                      <DropdownMenuRadioItem key={language.code} value={language.code} className="gap-2">
                        <span>{language.flag}</span>
                        <span>{language.name}</span>
                      </DropdownMenuRadioItem>
                    ))}
                  </DropdownMenuRadioGroup>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="gap-2 text-destructive"
                onClick={() => {
                  if (!isLoading) {
                    void logout();
                  }
                }}
              >
                <LogOut className="h-4 w-4" />
                <span>{t('navigation.logout')}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      }
    >
      <div className="container mx-auto px-4 py-8 lg:px-6">
        <Outlet />
      </div>
    </PortalShell>
  );
}
